import { useCallback, useEffect } from "react";
import type {
  Artifact,
  Attachment,
  McpServerDefinition,
  McpToolDefinition,
} from "@lumen/shared";
import type { RichTextFormat } from "./RichTextInput";
import { api } from "../../api";
import { t } from "../../i18n";
import type { ComposerInteractionContext } from "./ComposerContextTypes";
import type { WorkspaceFileReference } from "./ComposerContextTypes";
import type { ComposerSkill } from "./Composer";

export function useComposerInteractionHandlers(
  context: ComposerInteractionContext,
) {
  const {
    acceptsTripoMultiview,
    caretText,
    files,
    mcpServerIds,
    mcpTools,
    richInput,
    setArtifactDismissed,
    setArtifactIndex,
    setFiles,
    setMcpServers,
    setMcpTools,
    setMcpToolSelections,
    setSkillIndex,
    setSlashDismissed,
    setToolSaveStatus,
    setTools,
    setUploading,
    setValue,
    toolSaveTimers,
  } = context;
  const chooseSkill = (skill: ComposerSkill) => {
    richInput.current?.insertMention({
      reference: `/${skill.slug}`,
      label: `/${skill.slug}`,
      kind: "skill",
    });
    setSkillIndex(0);
    setSlashDismissed(true);
    requestAnimationFrame(() => richInput.current?.focus());
  };
  const chooseArtifact = (artifact: Artifact) => {
    richInput.current?.insertMention({
      reference: `@artifact:${artifact.id}`,
      label: artifact.name,
      kind: "artifact",
    });
    setArtifactIndex(0);
    setArtifactDismissed(true);
    requestAnimationFrame(() => richInput.current?.focus());
  };
  const chooseWorkspaceFile = (file: WorkspaceFileReference) => {
    richInput.current?.insertMention({
      reference: `@${file.path}`,
      label: `@${file.path}`,
      kind: "file",
    });
    setArtifactIndex(0);
    setArtifactDismissed(true);
    requestAnimationFrame(() => richInput.current?.focus());
  };
  const updateValue = (next: string) => {
    setValue(next);
    setSkillIndex(0);
    setArtifactIndex(0);
    if (!/(?:^|\s)\/[a-z0-9-]*$/i.test(caretText)) setSlashDismissed(false);
    if (!/(^|\s)@(?:artifact:?)?[a-z0-9._~/-]*$/i.test(caretText))
      setArtifactDismissed(false);
  };
  const applyFormat = (kind: RichTextFormat) => richInput.current?.format(kind);
  const upload = async (incoming: File[]) => {
    if (!incoming.length) return;
    if (acceptsTripoMultiview) {
      const imageCount = files.filter((file) =>
        file.mimeType.startsWith("image/"),
      ).length;
      const incomingImages = incoming.filter((file) =>
        file.type.startsWith("image/"),
      );
      if (imageCount + incomingImages.length > 3) {
        window.alert(
          t("Tripo multiview accepts up to three reference images."),
        );
        return;
      }
    }
    setUploading(true);
    try {
      for (const file of incoming) {
        const data = new FormData();
        data.set("file", file);
        const uploaded = await api<Attachment>("/uploads", {
          method: "POST",
          body: data,
        });
        setFiles((current) => [...current, uploaded]);
      }
    } finally {
      setUploading(false);
    }
  };
  const updateToolSettings = (
    toolId: string,
    userSettings: Record<string, unknown>,
  ) => {
    setTools((current) =>
      current.map((tool) =>
        tool.id === toolId ? { ...tool, userSettings } : tool,
      ),
    );
    const currentTimer = toolSaveTimers.current.get(toolId);
    if (currentTimer) window.clearTimeout(currentTimer);
    setToolSaveStatus((current) => ({ ...current, [toolId]: "saving" }));
    toolSaveTimers.current.set(
      toolId,
      window.setTimeout(() => {
        toolSaveTimers.current.delete(toolId);
        void api(`/tools/${toolId}/settings`, {
          method: "PUT",
          body: JSON.stringify(userSettings),
        })
          .then(() =>
            setToolSaveStatus((current) => ({
              ...current,
              [toolId]: "saved",
            })),
          )
          .catch(() =>
            setToolSaveStatus((current) => ({
              ...current,
              [toolId]: "error",
            })),
          );
      }, 350),
    );
  };
  const loadMcpTools = useCallback(
    async (serverId: string) => {
      if (
        mcpTools[serverId]?.status === "loading" ||
        mcpTools[serverId]?.status === "ready"
      )
        return;
      setMcpTools((current) => ({
        ...current,
        [serverId]: { status: "loading", tools: [] },
      }));
      try {
        const rows = await api<McpToolDefinition[]>(
          `/mcp-servers/${serverId}/tools`,
        );
        setMcpTools((current) => ({
          ...current,
          [serverId]: { status: "ready", tools: rows },
        }));
        setMcpToolSelections((current) =>
          Object.hasOwn(current, serverId)
            ? current
            : { ...current, [serverId]: rows.map((tool) => tool.name) },
        );
      } catch (reason) {
        setMcpTools((current) => ({
          ...current,
          [serverId]: {
            status: "error",
            tools: [],
            error:
              reason instanceof Error
                ? reason.message
                : "Could not load MCP functions",
          },
        }));
      }
    },
    [mcpTools, setMcpToolSelections, setMcpTools],
  );
  // Loading is keyed by server ID and guarded against duplicate requests above.
  useEffect(() => {
    for (const serverId of mcpServerIds) void loadMcpTools(serverId);
  }, [loadMcpTools, mcpServerIds]);
  const setupMcp = async (server: McpServerDefinition) => {
    try {
      if (server.authType === "oauth") {
        const result = await api<{ authorizationUrl: string }>(
          `/mcp-servers/${server.id}/oauth/start`,
          {
            method: "POST",
            body: JSON.stringify({
              returnTo: window.location.pathname + window.location.search,
            }),
          },
        );
        window.location.assign(result.authorizationUrl);
        return;
      }
      const label =
        server.authType === "bearer"
          ? "Bearer token"
          : server.authType === "api-key"
            ? "API key"
            : "Custom headers JSON";
      const value = window.prompt(`Enter ${label} for ${server.name}`);
      if (!value) return;
      let config: Record<string, unknown>;
      try {
        config =
          server.authType === "bearer"
            ? { token: value }
            : server.authType === "api-key"
              ? { apiKey: value }
              : { headers: JSON.parse(value) };
      } catch {
        window.alert("Custom headers must be a valid JSON object.");
        return;
      }
      await api(`/mcp-servers/${server.id}/credentials`, {
        method: "PUT",
        body: JSON.stringify({ config }),
      });
      setMcpServers((current) =>
        current.map((row) =>
          row.id === server.id ? { ...row, userConfigConfigured: true } : row,
        ),
      );
    } catch (reason) {
      window.alert(
        reason instanceof Error ? reason.message : "MCP setup failed",
      );
    }
  };
  return {
    chooseSkill,
    chooseArtifact,
    chooseWorkspaceFile,
    updateValue,
    applyFormat,
    upload,
    updateToolSettings,
    loadMcpTools,
    setupMcp,
  };
}
